import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { listCorpora, type CorpusSummary } from "../../../api/content/corporaApi";
import { useProjects } from "../../../projects/projectContext";
import { useLoad } from "../../../shared/loading/useLoad";
import { readStoredString, writeStoredString } from "../../../shared/storage/storedString";
import { readStoredStringSet, writeStoredStringSet } from "../../../shared/storage/storedStringSet";
import { CorpusSelectionContext, type CorpusSelectionValue } from "./corpusSelectionContext";

const storageKeyOf = (projectId: string) => `dmw.selectedCorpus.${projectId}`;
const hiddenStorageKeyOf = (projectId: string) => `dmw.hiddenCorpora.${projectId}`;
/** Changes when a pane reads the corpora again after a change, so the other panes read them too. */
const changedStorageKeyOf = (projectId: string) => `dmw.corporaChanged.${projectId}`;
const storedHidden = (projectId: string) => readStoredStringSet(hiddenStorageKeyOf(projectId)) ?? new Set<string>();

const NO_CORPORA = (): Promise<readonly CorpusSummary[]> => Promise.resolve([]);

/** Loads the chosen project's corpora; remembers the chosen and the hidden corpora per project in the browser. */
export default function CorpusSelectionProvider({ children }: { children: ReactNode }) {
  const { selectedProjectId } = useProjects();
  const [choice, setChoice] = useState<{ readonly projectId: string; readonly corpusId: string } | null>(null);

  const load = useCallback(
    (signal: AbortSignal) => (selectedProjectId === null ? NO_CORPORA() : listCorpora(selectedProjectId, signal)),
    [selectedProjectId],
  );
  const { state: corpora, reload } = useLoad(load);
  const reloadCorpora = useCallback(() => {
    reload();
    if (selectedProjectId !== null) writeStoredString(changedStorageKeyOf(selectedProjectId), new Date().toISOString());
  }, [reload, selectedProjectId]);

  const chosenCorpusId =
    selectedProjectId === null
      ? null
      : choice?.projectId === selectedProjectId
        ? choice.corpusId
        : readStoredString(storageKeyOf(selectedProjectId));

  const selectCorpus = useCallback(
    (corpusId: string) => {
      if (selectedProjectId === null) {
        return;
      }
      writeStoredString(storageKeyOf(selectedProjectId), corpusId);
      setChoice({ projectId: selectedProjectId, corpusId });
    },
    [selectedProjectId],
  );

  const [hidden, setHidden] = useState<{ readonly projectId: string; readonly corpusIds: ReadonlySet<string> } | null>(null);
  const hiddenCorpusIds = useMemo<ReadonlySet<string>>(
    () => (selectedProjectId === null ? new Set() : hidden?.projectId === selectedProjectId ? hidden.corpusIds : storedHidden(selectedProjectId)),
    [hidden, selectedProjectId],
  );
  // The split view's panes are separate windows: each follows the other's stored choices, and reads
  // the corpora again when the other changed them, as an import there does.
  useEffect(() => {
    if (selectedProjectId === null) {
      return undefined;
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === hiddenStorageKeyOf(selectedProjectId)) {
        setHidden({ projectId: selectedProjectId, corpusIds: storedHidden(selectedProjectId) });
      }
      if (event.key === storageKeyOf(selectedProjectId) && event.newValue !== null) {
        setChoice({ projectId: selectedProjectId, corpusId: event.newValue });
      }
      if (event.key === null || event.key === changedStorageKeyOf(selectedProjectId)) {
        reload();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => { window.removeEventListener("storage", onStorage); };
  }, [selectedProjectId, reload]);

  const showCorpus = useCallback(
    (corpusId: string, shown: boolean) => {
      if (selectedProjectId === null) {
        return;
      }
      // From storage, not this window's copy, so another pane's choice is kept.
      const next = storedHidden(selectedProjectId);
      if (shown) next.delete(corpusId);
      else next.add(corpusId);
      writeStoredStringSet(hiddenStorageKeyOf(selectedProjectId), next);
      setHidden({ projectId: selectedProjectId, corpusIds: next });
    },
    [selectedProjectId],
  );

  const value = useMemo<CorpusSelectionValue>(() => {
    const loaded = corpora.status === "loaded" ? corpora.value : [];
    // The remembered corpus if it still exists, otherwise the project's first.
    const selectedCorpus = loaded.find((corpus) => corpus.corpusId === chosenCorpusId) ?? loaded[0] ?? null;
    return {
      corpora: selectedProjectId === null ? null : corpora,
      selectedCorpus,
      selectCorpus,
      hiddenCorpusIds,
      showCorpus,
      reloadCorpora,
    };
  }, [corpora, chosenCorpusId, selectedProjectId, selectCorpus, hiddenCorpusIds, showCorpus, reloadCorpora]);

  return <CorpusSelectionContext.Provider value={value}>{children}</CorpusSelectionContext.Provider>;
}
